/**
 * Pretext core + hyphenation pattern bundles (see lazyloadable_js).
 * Hyphenation is split: germanic (de+nl), roman (fr+es+it+pt), english (en-us).
 */

/** Animation types that use pretext_line_split (must stay in sync with pretext_line_split.js). */
const PRETEXT_LAZY_ANIMATION_TYPES = new Set(['masked_lines', 'fade_lines', 'typewriter_lines', 'masked_words', 'fade_words', 'typewriter_words', 'fade_characters'])

const HYPHENATION_BUNDLE_CONFIG = {
	germanic: {
		scriptId: 'lay-pretext-hyphenation-germanic',
		filename: 'pretext_hyphenation_germanic.min.js',
	},
	roman: {
		scriptId: 'lay-pretext-hyphenation-roman',
		filename: 'pretext_hyphenation_roman.min.js',
	},
	en: {
		scriptId: 'lay-pretext-hyphenation-en',
		filename: 'pretext_hyphenation_en.min.js',
	},
}

export function targetNeedsPretextLazyModule(target) {
	const anim = target.getAttribute('data-animation-type')
	if (!anim || anim === 'fade_whole_text' || anim === 'fade_and_slide_whole_text') {
		return false
	}
	return PRETEXT_LAZY_ANIMATION_TYPES.has(anim)
}

export function someVisibleTargetNeedsPretextLazyModule(targets) {
	return targets.some((target) => {
		if (!jQuery(target).is(':visible')) {
			return false
		}
		return targetNeedsPretextLazyModule(target)
	})
}

function getPageLanguageFallback() {
	if (document.body) {
		const bodyLangClass = Array.from(document.body.classList).find((className) => className.indexOf('language-') === 0)
		if (bodyLangClass) {
			return bodyLangClass.replace(/^language-/, '').toLowerCase()
		}
	}
	const htmlLang = (document.documentElement.lang || '').toLowerCase()
	if (htmlLang) {
		return htmlLang
	}
	return 'en'
}

/**
 * @param {string} lang BCP47 / html lang string
 * @returns {'germanic' | 'roman' | 'en'}
 */
function langStringToHyphenationBundleId(lang) {
	const l = (lang || '').toLowerCase().trim()
	if (!l) {
		return langStringToHyphenationBundleId(getPageLanguageFallback())
	}
	if (l.startsWith('de') || l.startsWith('nl')) {
		return 'germanic'
	}
	if (l.startsWith('fr') || l.startsWith('es') || l.startsWith('it') || l.startsWith('pt')) {
		return 'roman'
	}
	return 'en'
}

/**
 * Which hyphenation bundles are needed for the given text-animation targets (visible + pretext types).
 * @param {HTMLElement[]} targets
 * @returns {Set<string>} keys: germanic | roman | en
 */
export function collectHyphenationBundleIdsForTargets(targets) {
	const ids = new Set()
	for (let i = 0; i < targets.length; i++) {
		const target = targets[i]
		if (!jQuery(target).is(':visible')) {
			continue
		}
		if (!targetNeedsPretextLazyModule(target)) {
			continue
		}
		const langHost = target.closest('[lang]')
		const explicit = langHost ? langHost.getAttribute('lang') || '' : ''
		const resolved = explicit.trim() ? explicit : getPageLanguageFallback()
		ids.add(langStringToHyphenationBundleId(resolved))
	}
	if (ids.size === 0) {
		ids.add(langStringToHyphenationBundleId(getPageLanguageFallback()))
	}
	return ids
}

function getTemplateUri() {
	return typeof window.layData !== 'undefined' && window.layData.template_directory_uri ? window.layData.template_directory_uri : ''
}

function appendAssetVersion(src) {
	const version = typeof window.layData !== 'undefined' ? window.layData.asset_version : ''
	if (!version) {
		return src
	}
	return src + (src.indexOf('?') === -1 ? '?' : '&') + 'ver=' + encodeURIComponent(version)
}

function waitForPretextApi(timeoutMs) {
	const start = Date.now()
	return new Promise((resolve, reject) => {
		function tick() {
			if (window.layPretextTextAnimation && typeof window.layPretextTextAnimation.registerHyphenationPatterns === 'function') {
				resolve(window.layPretextTextAnimation)
				return
			}
			if (Date.now() - start > timeoutMs) {
				reject(new Error('Pretext text animation API missing after script load'))
				return
			}
			requestAnimationFrame(tick)
		}
		tick()
	})
}

function loadScriptById(scriptId, src) {
	return new Promise((resolve, reject) => {
		if (document.getElementById(scriptId)) {
			resolve()
			return
		}
		const s = document.createElement('script')
		s.id = scriptId
		s.src = src
		s.async = true
		s.onload = () => resolve()
		s.onerror = () => reject(new Error(`Failed to load ${src}`))
		document.body.appendChild(s)
	})
}

function ensureHyphenationBundles(bundleIds) {
	const base = getTemplateUri()
	const keys = [...bundleIds]
	return Promise.all(
		keys.map((key) => {
			const cfg = HYPHENATION_BUNDLE_CONFIG[key]
			if (!cfg) {
				return Promise.resolve()
			}
			return loadScriptById(cfg.scriptId, appendAssetVersion(base + '/frontend/assets/js/' + cfg.filename))
		}),
	)
}

let coreInflight = null

function loadPretextCoreIfNeeded() {
	if (window.layPretextTextAnimation && typeof window.layPretextTextAnimation.registerHyphenationPatterns === 'function') {
		return Promise.resolve(window.layPretextTextAnimation)
	}
	if (!coreInflight) {
		coreInflight = (async () => {
			const id = 'lay-pretext-textanimation'
			const base = getTemplateUri()
			const src = appendAssetVersion(base + '/frontend/assets/js/pretext_textanimation.min.js')
			if (!document.getElementById(id)) {
				await new Promise((resolve, reject) => {
					const s = document.createElement('script')
					s.id = id
					s.src = src
					s.async = true
					s.onload = resolve
					s.onerror = () => reject(new Error('Pretext text animation script failed to load'))
					document.body.appendChild(s)
				})
			}
			return await waitForPretextApi(15000)
		})().finally(() => {
			coreInflight = null
		})
	}
	return coreInflight
}

/**
 * Kick off the same dynamic script loads as prepareTexts during SPA navigations (and CPL swap).
 * Initial load: frontend.critical-init already injects these scripts on newpageshownfast (before frontend.app).
 * Uses data-animation-type only (not :visible) so fetch starts even while CPL/desktop branches are still hidden.
 */
export function prefetchLayPretextLazyModulesEarly() {
	if (typeof document === 'undefined') {
		return
	}
	const hinted = document.querySelectorAll('.lay-text-animation[data-animation-type]')
	let needsPrefetch = false
	for (let i = 0; i < hinted.length; i++) {
		if (targetNeedsPretextLazyModule(hinted[i])) {
			needsPrefetch = true
			break
		}
	}
	if (!needsPrefetch) {
		return
	}
	const targets = Array.from(document.querySelectorAll('.lay-text-animation'))
	ensureLayPretextTextAnimationLoaded(targets).catch(() => {})
}

/**
 * @param {HTMLElement[]=} targets Text animation roots (same as prepareTexts); drives which hyphenation bundle loads.
 * @returns {Promise<object>}
 */
export function ensureLayPretextTextAnimationLoaded(targets) {
	return loadPretextCoreIfNeeded()
		.then(() => ensureHyphenationBundles(collectHyphenationBundleIdsForTargets(targets || [])))
		.then(() => window.layPretextTextAnimation)
}
